/**
 * Enhanced PermissionGate - Enterprise Security Component (Refactored)
 *
 * Tamper-proof permission component with comprehensive security hardening.
 * Provides fail-secure permission checking with DOM tamper detection,
 * automatic re-validation, and security event emission.
 *
 * Security Features:
 * - Fail-secure by default (deny if uncertain)
 * - DOM mutation observer for tamper detection
 * - Automatic re-validation on visibility change
 * - Server-side fallback on client-side failure
 * - Comprehensive audit logging
 * - OWASP Top 10 2024 compliance
 */

import React, {
  useEffect,
  useRef,
  useCallback,
  ReactNode
} from 'react';
import { SecurityEvent } from '../../lib/permissions/services/permission-integrity';
import {
  PermissionRequirement,
  PermissionResource,
  PermissionAction
} from '../../lib/permissions/types';

// Import extracted hooks
import { usePermissionGate, SecurityLevel } from './hooks/usePermissionGate';
import { useSecurityMonitoring } from './hooks/useSecurityMonitoring';

// Fallback strategies for denied permissions
export type FallbackStrategy = 'hidden' | 'disabled' | 'tooltip' | 'message' | 'redirect';

export interface PermissionGateProps {
  // Core permission props
  requires?: string;
  action?: PermissionAction;
  resource?: PermissionResource;
  resourceId?: string;
  requirement?: PermissionRequirement;

  // Fallback configuration
  fallback?: FallbackStrategy;
  fallbackMessage?: string;
  fallbackComponent?: ReactNode;
  redirectTo?: string;

  // Security configuration
  securityLevel?: SecurityLevel;
  auditLog?: boolean;
  serverSideValidation?: boolean;
  revalidationInterval?: number;
  tamperDetection?: boolean;

  // Component props
  children: ReactNode;
  className?: string;
  'data-testid'?: string;

  // Event handlers
  onPermissionDenied?: (_reason: string, _requirement: PermissionRequirement) => void;
  onSecurityViolation?: (_event: SecurityEvent) => void;
  onTamperDetected?: (_details: Record<string, unknown>) => void;
}

const PermissionGateComponent: React.FC<PermissionGateProps> = ({
  requires,
  action,
  resource,
  resourceId,
  requirement: propRequirement,
  fallback = 'hidden',
  fallbackMessage,
  fallbackComponent,
  redirectTo,
  securityLevel = 'standard',
  auditLog = true,
  serverSideValidation = false,
  revalidationInterval = 0,
  tamperDetection: _tamperDetection = true,
  children,
  className,
  'data-testid': testId,
  onPermissionDenied,
  onSecurityViolation,
  onTamperDetected: _onTamperDetected
}) => {
  // Refs for DOM monitoring
  const containerRef = useRef<HTMLDivElement>(null);

  // Initialize permission gate logic with filtered undefined values
  const permissionGateProps = {
    securityLevel,
    auditLog,
    serverSideValidation,
    ...(requires !== undefined && { requires }),
    ...(action !== undefined && { action }),
    ...(resource !== undefined && { resource }),
    ...(resourceId !== undefined && { resourceId }),
    ...(propRequirement !== undefined && { requirement: propRequirement }),
  };

  const {
    isAllowed,
    isLoading,
    checkPermission,
    buildRequirement,
    isInitialized,
  } = usePermissionGate(permissionGateProps);

  // Initialize security monitoring with filtered undefined values
  const securityMonitoringProps = {
    securityLevel,
    onVisibilityRevalidation: checkPermission,
    revalidationInterval,
    ...(onSecurityViolation !== undefined && { onSecurityViolation }),
  };

  const {
    securityViolation,
    setupVisibilityRevalidation,
    setupPeriodicRevalidation,
    setupSecurityEventListener,
    updateLastValidation,
    cleanup,
  } = useSecurityMonitoring(securityMonitoringProps);

  // Handle permission denied callback
  const handlePermissionDenied = useCallback(() => {
    const requirement = buildRequirement();
    if (!isAllowed && requirement && onPermissionDenied) {
      onPermissionDenied(
        fallbackMessage || 'Permission denied',
        requirement
      );
    }
  }, [isAllowed, buildRequirement, onPermissionDenied, fallbackMessage]);

  // Initialize component - only run once on mount and when core props change
  useEffect(() => {
    if (!isInitialized) {
      checkPermission();
    }
  }, [requires, action, resource, resourceId, propRequirement, isInitialized, checkPermission]);

  // Setup security monitoring - separate effect to avoid dependency conflicts
  useEffect(() => {
    updateLastValidation();

    const cleanupSecurityListener = setupSecurityEventListener();

    // Setup security features based on security level
    if (securityLevel === 'high' || securityLevel === 'maximum') {
      setupVisibilityRevalidation(containerRef.current);
    }

    setupPeriodicRevalidation();

    // Cleanup function
    return () => {
      cleanupSecurityListener();
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [securityLevel]); // Only re-run when security level changes

  // Handle permission denied callback
  useEffect(() => {
    handlePermissionDenied();
  }, [handlePermissionDenied]);

  // Handle security violation
  if (securityViolation) {
    return (
      <div
        className="permission-gate-security-violation"
        data-testid={testId ? `${testId}-security-violation` : undefined}
      >
        <div className="text-red-600 font-medium">
          Security violation detected. Please refresh the page.
        </div>
      </div>
    );
  }

  // Handle loading state - prevent flickering by hiding content until permission is resolved
  if (isLoading) {

    // Always check fallbackComponent first, regardless of fallback value
    // This prevents flickering by showing the same content that will appear if denied
    if (fallbackComponent) {
      return <>{fallbackComponent}</>;
    }

    // For 'hidden' fallback, don't show anything while checking permissions
    // This prevents the brief flash of protected content
    if (fallback === 'hidden') {
      return null;
    }

    // For other fallback types, show a minimal placeholder
    // This maintains layout without revealing protected content
    return (
      <div
        className="permission-gate-loading"
        data-testid={testId ? `${testId}-loading` : undefined}
        style={{ opacity: 0.3, pointerEvents: 'none' }}
      >
        <div style={{ filter: 'blur(2px)' }}>
          {children}
        </div>
      </div>
    );
  }

  // Handle permission denied
  if (isAllowed === false) {
    switch (fallback) {
      case 'hidden':
        return null;

      case 'disabled':
        return (
          <div
            ref={containerRef}
            className={`permission-gate-disabled ${className || ''}`}
            data-testid={testId ? `${testId}-disabled` : undefined}
            data-permission-allowed="false"
            data-security-level={securityLevel}
            style={{ opacity: 0.5, pointerEvents: 'none' }}
          >
            {children}
          </div>
        );

      case 'tooltip':
        return (
          <div
            ref={containerRef}
            className={`permission-gate-tooltip ${className || ''}`}
            data-testid={testId ? `${testId}-tooltip` : undefined}
            data-permission-allowed="false"
            data-security-level={securityLevel}
            title={fallbackMessage || 'Permission denied'}
          >
            <div style={{ opacity: 0.5, pointerEvents: 'none' }}>
              {children}
            </div>
          </div>
        );

      case 'message':
        return (
          <div
            ref={containerRef}
            className={`permission-gate-message ${className || ''}`}
            data-testid={testId ? `${testId}-message` : undefined}
            data-permission-allowed="false"
            data-security-level={securityLevel}
          >
            {fallbackComponent || (
              <div className="text-gray-500 italic">
                {fallbackMessage || 'You do not have permission to access this resource.'}
              </div>
            )}
          </div>
        );

      case 'redirect':
        if (redirectTo) {
          window.location.href = redirectTo;
        }
        return null;

      default:
        return null;
    }
  }

  // Permission granted - render children
  return (
    <div
      ref={containerRef}
      className={`permission-gate-allowed ${className || ''}`}
      data-testid={testId}
      data-permission-allowed="true"
      data-security-level={securityLevel}
    >
      {children}
    </div>
  );
};

// Memoized version for performance optimization
export const PermissionGate = React.memo(PermissionGateComponent, (prevProps, nextProps) => {
  // Custom comparison for better performance
  return (
    prevProps.requires === nextProps.requires &&
    prevProps.action === nextProps.action &&
    prevProps.resource === nextProps.resource &&
    prevProps.resourceId === nextProps.resourceId &&
    prevProps.fallback === nextProps.fallback &&
    prevProps.securityLevel === nextProps.securityLevel &&
    prevProps.auditLog === nextProps.auditLog &&
    prevProps.tamperDetection === nextProps.tamperDetection &&
    prevProps.children === nextProps.children
  );
});

// Export types
export type { SecurityLevel };

// Export default for convenience
export default PermissionGate;